/**
 * doorGeometry.ts — door-as-width-rectangle-on-the-wall geometry (Slice 3 Stage 3b).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHY THIS EXISTS (owner correction 2026-06-07)
 * ─────────────────────────────────────────────────────────────────────────
 *  Stage 3 modelled an entrance as a single point-pin → one pada. The owner
 *  rejected it: a real door has WIDTH, sits ON the wall as a RECTANGLE, and can
 *  span MULTIPLE padas. This file recovers the pre-rebuild Sprint-A.2 model
 *  (`src/_legacy/lib/intakeGeometry.ts` + `types/intake.ts` EntranceMarker):
 *  a door is parameterised by which boundary EDGE it snaps to (`edge_index`),
 *  where along that edge its centre sits (`parametric_t` ∈ [0,1]), and its
 *  opening WIDTH in image pixels (`width_image_px`).
 *
 *  OWNER RULE (LOCKED): a door counts in EVERY pada its width overlaps — one
 *  entrance finding per covered pada. So the door's two end-points along the
 *  wall are computed (centre ± width/2, walked along the boundary, spilling
 *  across vertices when needed), the span is sampled, and EVERY pada the span
 *  touches is collected (unique, in order).
 *
 *  REUSABLE FOR STAGE-4 AUO: `cellsForSegment` (and its point-set core
 *  `cellsForPoints`) are deliberately generic — given a polyline of sample
 *  points and a labelled set of polygons (the grid's `entrancePolys` here, the
 *  16 `zonePolys` for AUO), they return the unique labels the path overlaps, in
 *  first-touched order. Stage-4 will reuse `cellsForPoints` to map an AUO
 *  rectangle → the zones it covers.
 *
 *  COORDINATE SPACE: everything is IMAGE-PIXEL space (OQ-C), the same space the
 *  boundary + grid live in. No transform.
 *
 *  PURE — no Firebase, no DOM. Unit-tested via _tsload.
 * ─────────────────────────────────────────────────────────────────────────
 */
import { pointInPolygon, type Pt } from './vastuGrid';

/* ───────────────────────── edge math (recovered) ───────────────────────── */

/** Nearest boundary edge to a click, with the snapped point + parametric t. */
export interface EdgeHit {
  edge_index: number;
  parametric_t: number;
  /** Snapped point on the boundary edge. */
  x: number;
  y: number;
  /** Perpendicular distance from click to the segment. */
  distance: number;
}

/** Snap a click `p` to the nearest boundary segment. Verbatim recovered math. */
export function nearestEdge(p: Pt, boundary: Pt[]): EdgeHit | null {
  if (!Array.isArray(boundary) || boundary.length < 2) return null;
  let best: EdgeHit | null = null;
  for (let i = 0; i < boundary.length; i++) {
    const a = boundary[i];
    const b = boundary[(i + 1) % boundary.length];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len2 = dx * dx + dy * dy || 1e-12;
    let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
    t = Math.max(0, Math.min(1, t));
    const sx = a.x + dx * t;
    const sy = a.y + dy * t;
    const d = Math.hypot(p.x - sx, p.y - sy);
    if (!best || d < best.distance) {
      best = { edge_index: i, parametric_t: t, x: sx, y: sy, distance: d };
    }
  }
  return best;
}

/** Project a click onto a SPECIFIC edge (used during an edge-slide drag). */
export function pointOnEdge(
  p: Pt,
  boundary: Pt[],
  edgeIndex: number,
): { x: number; y: number; parametric_t: number } | null {
  if (!Array.isArray(boundary) || boundary.length < 2) return null;
  const a = boundary[edgeIndex % boundary.length];
  const b = boundary[(edgeIndex + 1) % boundary.length];
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy || 1e-12;
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  return { x: a.x + dx * t, y: a.y + dy * t, parametric_t: t };
}

/** Reconstruct the {x,y} point from edge_index + parametric_t. */
export function edgePointAt(
  boundary: Pt[],
  edgeIndex: number,
  t: number,
): Pt | null {
  if (!Array.isArray(boundary) || boundary.length < 2) return null;
  const a = boundary[edgeIndex % boundary.length];
  const b = boundary[(edgeIndex + 1) % boundary.length];
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}

/** Length of one boundary edge (image px). */
function edgeLength(boundary: Pt[], edgeIndex: number): number {
  const a = boundary[edgeIndex % boundary.length];
  const b = boundary[(edgeIndex + 1) % boundary.length];
  return Math.hypot(b.x - a.x, b.y - a.y);
}

/* ───────────────────────── walk along the perimeter ───────────────────────── */

/**
 * Walk `dist` pixels along the closed boundary perimeter, starting at the point
 * (edge_index, parametric_t), in the given direction (+1 forward = vertex
 * order, −1 backward). Returns the point reached — SPILLING ACROSS VERTICES so
 * a door near a corner straddles two walls correctly. Clamps at the polygon's
 * own perimeter length (never loops more than once).
 *
 * PURE. The recovered Sprint-A.2 width handle moved straight off the edge
 * normal; this walk-the-perimeter version is what makes "every covered pada"
 * correct when a wide door spills past a corner.
 */
export function walkPerimeter(
  boundary: Pt[],
  edgeIndex: number,
  parametricT: number,
  dist: number,
  dir: 1 | -1,
): Pt {
  const n = boundary.length;
  // Start point.
  let ei = ((edgeIndex % n) + n) % n;
  let t = Math.max(0, Math.min(1, parametricT));
  let remaining = Math.max(0, dist);

  // Total perimeter as a clamp so we never spin forever on a tiny polygon.
  let perim = 0;
  for (let i = 0; i < n; i++) perim += edgeLength(boundary, i);
  if (perim <= 1e-9) return edgePointAt(boundary, ei, t) || boundary[0];
  remaining = Math.min(remaining, perim);

  while (remaining > 1e-9) {
    const len = edgeLength(boundary, ei) || 1e-12;
    if (dir === 1) {
      const toEnd = (1 - t) * len; // pixels to this edge's far vertex
      if (remaining <= toEnd) {
        t += remaining / len;
        remaining = 0;
      } else {
        remaining -= toEnd;
        ei = (ei + 1) % n;
        t = 0;
      }
    } else {
      const toStart = t * len; // pixels back to this edge's near vertex
      if (remaining <= toStart) {
        t -= remaining / len;
        remaining = 0;
      } else {
        remaining -= toStart;
        ei = (ei - 1 + n) % n;
        t = 1;
      }
    }
  }
  return edgePointAt(boundary, ei, t) || boundary[0];
}

/** Total perimeter length of the closed boundary (image px). */
export function perimeterLength(boundary: Pt[]): number {
  if (!Array.isArray(boundary) || boundary.length < 2) return 0;
  let p = 0;
  for (let i = 0; i < boundary.length; i++) p += edgeLength(boundary, i);
  return p;
}

/**
 * Cumulative arc-length (image px) from boundary vertex 0, walking forward, to
 * the point (edge_index, parametric_t). The canonical 1-D coordinate of a point
 * on the perimeter — lets us measure door ends as distances and rebuild a door
 * from its two ends. Pure.
 */
export function perimeterPos(
  boundary: Pt[],
  edgeIndex: number,
  parametricT: number,
): number {
  if (!Array.isArray(boundary) || boundary.length < 2) return 0;
  const n = boundary.length;
  const ei = ((edgeIndex % n) + n) % n;
  let pos = 0;
  for (let i = 0; i < ei; i++) pos += edgeLength(boundary, i);
  pos += Math.max(0, Math.min(1, parametricT)) * edgeLength(boundary, ei);
  return pos;
}

/**
 * Inverse of `perimeterPos`: a cumulative arc-length → (edge_index, parametric_t).
 * Wraps around the closed perimeter. Pure.
 */
export function posToEdgeT(
  boundary: Pt[],
  pos: number,
): { edge_index: number; parametric_t: number } {
  if (!Array.isArray(boundary) || boundary.length < 2) {
    return { edge_index: 0, parametric_t: 0 };
  }
  const perim = perimeterLength(boundary);
  if (perim <= 1e-9) return { edge_index: 0, parametric_t: 0 };
  let p = ((pos % perim) + perim) % perim; // wrap into [0, perim)
  for (let i = 0; i < boundary.length; i++) {
    const len = edgeLength(boundary, i) || 1e-12;
    if (p <= len || i === boundary.length - 1) {
      return { edge_index: i, parametric_t: Math.max(0, Math.min(1, p / len)) };
    }
    p -= len;
  }
  return { edge_index: 0, parametric_t: 0 };
}

/**
 * PURE — rebuild a door (centre edge/t + width) from its TWO end positions on
 * the perimeter (cumulative arc-lengths). Used by the on-drawing end-grips
 * (item 4): the owner drags one end; we keep the OTHER end anchored and recompute
 * the door's centre + width from the (anchor, moved-end) pair. The centre sits at
 * the midpoint ALONG the perimeter (so the door still rides the wall, spilling
 * across corners); width = the arc-length between the two ends. Picks the SHORTER
 * arc between the two ends (a door never wraps >half the building).
 */
export function doorFromEnds(
  boundary: Pt[],
  posA: number,
  posB: number,
): { edge_index: number; parametric_t: number; width_image_px: number } {
  const perim = perimeterLength(boundary);
  if (perim <= 1e-9) return { edge_index: 0, parametric_t: 0, width_image_px: 0 };
  // normalise both onto [0, perim)
  const a = ((posA % perim) + perim) % perim;
  const b = ((posB % perim) + perim) % perim;
  let lo = Math.min(a, b);
  let hi = Math.max(a, b);
  let width = hi - lo;
  let mid = (lo + hi) / 2;
  // If the gap the SHORT way wraps the seam, use the wrap-around arc instead.
  if (width > perim - width) {
    width = perim - width;
    // midpoint of the wrapping arc: start at hi, go forward width/2 (wraps).
    mid = (hi + width / 2) % perim;
  }
  const et = posToEdgeT(boundary, mid);
  return {
    edge_index: et.edge_index,
    parametric_t: et.parametric_t,
    width_image_px: width,
  };
}

/* ───────────────────────── door span → points ───────────────────────── */

export interface DoorSpan {
  /** The door's centre on the wall (image px). */
  centre: Pt;
  /** The two ends of the door opening along the wall (image px). */
  start: Pt;
  end: Pt;
  /** Sampled points across the span (centre included), for cell testing. */
  samples: Pt[];
}

/**
 * Pull a point a few pixels toward `target` (the grid centroid). The door's
 * samples lie EXACTLY on the boundary edge, which is the outer edge of every
 * pada/zone wedge — point-in-polygon is unreliable on a polygon's own edge. A
 * tiny inward nudge lands the sample firmly INSIDE the wedge it belongs to so
 * coverage is robust. Pure.
 */
function nudgeInward(p: Pt, target: Pt | null, px: number): Pt {
  if (!target || px <= 0) return p;
  const dx = target.x - p.x;
  const dy = target.y - p.y;
  const len = Math.hypot(dx, dy);
  if (len < 1e-9) return p;
  const f = Math.min(px, len) / len;
  return { x: p.x + dx * f, y: p.y + dy * f };
}

/**
 * PURE — given a door (edge_index, parametric_t, width) on a boundary, compute
 * its centre, its two ends (centre ∓ width/2 walked along the perimeter, so a
 * door spilling past a corner is handled), and a set of sample points across
 * the span. `samplesPerSide` controls density (default 6 each side → 13 points
 * incl. centre) — enough to never skip a thin pada wedge at door scale.
 *
 * `inwardTarget` (the grid centroid) + `inwardPx` pull the SAMPLES (not the
 * rendered centre/start/end) slightly inside so point-in-polygon is robust on
 * the boundary edge. Pass the grid centroid for cell testing; omit for pure
 * geometry (rendering).
 */
export function doorSpan(
  boundary: Pt[],
  edgeIndex: number,
  parametricT: number,
  widthImagePx: number,
  samplesPerSide = 6,
  inwardTarget: Pt | null = null,
  inwardPx = 2,
): DoorSpan | null {
  if (!Array.isArray(boundary) || boundary.length < 2) return null;
  const centre = edgePointAt(boundary, edgeIndex, parametricT);
  if (!centre) return null;
  const half = Math.max(0, widthImagePx) / 2;
  const start = walkPerimeter(boundary, edgeIndex, parametricT, half, -1);
  const end = walkPerimeter(boundary, edgeIndex, parametricT, half, 1);

  const nudge = (p: Pt) => nudgeInward(p, inwardTarget, inwardPx);
  const samples: Pt[] = [];
  const k = Math.max(1, samplesPerSide);
  // back side (start → centre), then centre, then forward (centre → end)
  for (let i = k; i >= 1; i--) {
    samples.push(nudge(walkPerimeter(boundary, edgeIndex, parametricT, (half * i) / k, -1)));
  }
  samples.push(nudge(centre));
  for (let i = 1; i <= k; i++) {
    samples.push(nudge(walkPerimeter(boundary, edgeIndex, parametricT, (half * i) / k, 1)));
  }
  return { centre, start, end, samples };
}

/* ───────────────────────── reusable cell overlap (Stage-4 too) ───────────────────────── */

/**
 * PURE, GENERAL — given a list of sample points and a labelled set of polygons,
 * return the UNIQUE labels whose polygon contains at least one sample, in the
 * order each label is FIRST touched. Pulled slightly INSIDE each cell so a
 * sample landing exactly on a shared pada edge is attributed to the cell it is
 * mostly inside, not double-counted at the seam.
 *
 * This is the core reused by both the entrance "door span → padas" and the
 * Stage-4 AUO "rectangle → zones" mapping. `cells` = the grid's `entrancePolys`
 * (32 padas) or `zonePolys` (16 zones) as a `{label: polygon}` map / list.
 */
export function cellsForPoints(
  points: Pt[],
  cells: { label: string; poly: Pt[] }[],
): string[] {
  const seen = new Set<string>();
  const order: string[] = [];
  for (const p of points) {
    for (const c of cells) {
      if (pointInPolygon(p, c.poly)) {
        if (!seen.has(c.label)) {
          seen.add(c.label);
          order.push(c.label);
        }
        break; // a point belongs to exactly one cell — stop at the first hit
      }
    }
  }
  return order;
}

/**
 * PURE — the unique cell labels a door SPAN overlaps (the owner's "every covered
 * pada" rule). Convenience wrapper that builds the span then runs
 * `cellsForPoints`. Returns `[]` when the span is unplaceable or every sample
 * fell outside the boundary.
 */
export function cellsForSegment(
  boundary: Pt[],
  edgeIndex: number,
  parametricT: number,
  widthImagePx: number,
  cells: { label: string; poly: Pt[] }[],
  samplesPerSide = 6,
): string[] {
  // Every grid zone/pada polygon starts at the grid CENTROID (computeGrid builds
  // each poly as `[{cx,cy}, ...arc]`), so the first cell's first vertex IS the
  // centroid — use it as the inward-nudge target so samples land inside the
  // wedge, not on its outer boundary edge.
  const centroid =
    cells.length && cells[0].poly.length ? cells[0].poly[0] : null;
  const span = doorSpan(
    boundary,
    edgeIndex,
    parametricT,
    widthImagePx,
    samplesPerSide,
    centroid,
  );
  if (!span) return [];
  return cellsForPoints(span.samples, cells);
}

/** Convert a grid's `entrancePolys` / `zonePolys` map into the cells[] shape. */
export function cellsFromRecord(rec: Record<string, Pt[]>): { label: string; poly: Pt[] }[] {
  return Object.entries(rec).map(([label, poly]) => ({ label, poly }));
}
